import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}
interface MilestoneInfo {
  text: string;
  emoji: string;
  type: 'three-weeks' | 'two-weeks' | 'one-week';
}
function getMilestone(days: number): MilestoneInfo | null {
  if (days === 21) return {
    text: 'Noch 3 Wochen!',
    emoji: '🌴',
    type: 'three-weeks'
  };
  if (days === 14) return {
    text: 'Noch 2 Wochen!',
    emoji: '☀️',
    type: 'two-weeks'
  };
  if (days === 7) return {
    text: 'Noch 1 Woche!',
    emoji: '🎉',
    type: 'one-week'
  };
  return null;
}
function MilestoneBanner({
  milestone
}: {
  milestone: MilestoneInfo;
}) {
  if (milestone.type === 'three-weeks') {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.8
    }} animate={{
      opacity: 1,
      scale: 1,
      boxShadow: ['0 0 20px rgba(212, 165, 116, 0.4)', '0 0 40px rgba(212, 165, 116, 0.6)', '0 0 20px rgba(212, 165, 116, 0.4)']
    }} transition={{
      duration: 0.5,
      boxShadow: {
        duration: 2,
        repeat: Infinity
      }
    }} className="absolute top-24 left-1/2 -translate-x-1/2 z-20 px-8 py-4 rounded-full bg-gradient-to-r from-[#d4a574] to-[#c4704b] text-white" data-id="element-6">
        <span className="font-serif text-2xl md:text-3xl font-semibold" data-id="element-7">
          {milestone.text} {milestone.emoji}
        </span>
      </motion.div>;
  }
  if (milestone.type === 'two-weeks') {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.5
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="absolute top-24 left-1/2 -translate-x-1/2 z-20" data-id="element-8">
        <motion.div animate={{
        boxShadow: ['0 0 0 0px rgba(59, 158, 196, 0.4)', '0 0 0 20px rgba(59, 158, 196, 0)']
      }} transition={{
        duration: 1.5,
        repeat: Infinity
      }} className="px-8 py-4 rounded-full bg-gradient-to-r from-[#3b9ec4] to-[#2a7a9a] text-white" data-id="element-9">
          <span className="font-serif text-2xl md:text-3xl font-semibold" data-id="element-10">
            {milestone.text} {milestone.emoji}
          </span>
        </motion.div>
      </motion.div>;
  }
  if (milestone.type === 'one-week') {
    const particles = Array.from({
      length: 12
    }, (_, i) => i);
    return <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="absolute top-24 left-1/2 -translate-x-1/2 z-20" data-id="element-11">
        <div className="relative" data-id="element-12">
          {particles.map(i => <motion.div key={i} initial={{
          opacity: 0,
          scale: 0,
          x: 0,
          y: 0
        }} animate={{
          opacity: [0, 1, 0],
          scale: [0, 1, 0.5],
          x: Math.cos(i / 12 * Math.PI * 2) * 80,
          y: Math.sin(i / 12 * Math.PI * 2) * 80 - 40,
          rotate: Math.random() * 360
        }} transition={{
          duration: 2,
          repeat: Infinity,
          delay: i * 0.1,
          ease: 'easeOut'
        }} className="absolute top-1/2 left-1/2 w-3 h-3 rounded-full" style={{
          backgroundColor: ['#c4704b', '#d4a574', '#3b9ec4', '#faf6f1'][i % 4]
        }} data-id="element-13" />)}
          <motion.div animate={{
          scale: [1, 1.05, 1]
        }} transition={{
          duration: 0.5,
          repeat: Infinity
        }} className="px-8 py-4 rounded-full bg-gradient-to-r from-[#c4704b] to-[#d4a574] text-white relative z-10" data-id="element-14">
            <span className="font-serif text-2xl md:text-3xl font-semibold" data-id="element-15">
              {milestone.text} {milestone.emoji}
            </span>
          </motion.div>
        </div>
      </motion.div>;
  }
  return null;
}
function CountdownCard({
  value,
  label,
  delay,
  size = 'normal'
}: {
  value: number;
  label: string;
  delay: number;
  size?: 'large' | 'normal';
}) {
  const isLarge = size === 'large';
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.6,
    delay
  }} className="flex flex-col items-center" data-id="element-16">
      <div className={`bg-white/15 backdrop-blur-md border border-white/20 shadow-xl ${isLarge ? 'rounded-3xl px-10 py-8 md:px-16 md:py-12' : 'rounded-2xl px-4 py-4 md:px-6 md:py-6'}`} data-id="element-17">
        <motion.span key={value} initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className={`font-serif font-bold text-white tabular-nums ${isLarge ? 'text-7xl md:text-9xl lg:text-[10rem]' : 'text-3xl md:text-4xl lg:text-5xl'}`} data-id="element-18">
          {String(value).padStart(2, '0')}
        </motion.span>
      </div>
      <span className={`font-medium uppercase text-white/80 ${isLarge ? 'mt-5 text-sm md:text-base tracking-[0.3em]' : 'mt-3 text-xs md:text-sm tracking-[0.2em]'}`} data-id="element-19">
        {label}
      </span>
    </motion.div>;
}
const heroImages = ['https://cdn.magicpatterns.com/uploads/3oWHNxvXH6eKAkWiUrzdTg/IMG_3686.webp', 'https://cdn.magicpatterns.com/uploads/7jaijN8myS4xBjA2g2LnVW/IMG_3685.webp', 'https://cdn.magicpatterns.com/uploads/rYJZHg3Bx8jyA5a1vPsKQr/IMG_3681.jpg', 'https://cdn.magicpatterns.com/uploads/4TQc98ATeCzg2Lhr7YGwyV/IMG_3682.jpg', 'https://cdn.magicpatterns.com/uploads/bgRTcmVagNcha985nGgWCw/IMG_3687.webp', 'https://cdn.magicpatterns.com/uploads/odk81fdTZGRRuSSeTg73UW/IMG_3688.webp', 'https://cdn.magicpatterns.com/uploads/8fhZakEZrRwqmk1w18n3zw/IMG_3683.jpg', 'https://cdn.magicpatterns.com/uploads/qdWuXAnza7qwKbaSLqtA7b/IMG_3684.jpg'];
export function CountdownHero() {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const targetDate = new Date('2026-08-20T18:55:00+02:00');
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();
      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor(difference % (1000 * 60 * 60 * 24) / (1000 * 60 * 60));
        const minutes = Math.floor(difference % (1000 * 60 * 60) / (1000 * 60));
        const seconds = Math.floor(difference % (1000 * 60) / 1000);
        setTimeLeft({
          days,
          hours,
          minutes,
          seconds
        });
      }
    };
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    const slideTimer = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % heroImages.length);
    }, 10000);
    return () => clearInterval(slideTimer);
  }, []);
  const milestone = getMilestone(timeLeft.days);
  return <section className="relative min-h-screen w-full flex flex-col items-center justify-center overflow-hidden" data-id="element-20">
      {/* Background Slideshow */}
      <AnimatePresence mode="sync" data-id="element-21">
        <motion.div key={currentImageIndex} initial={{
        opacity: 0,
        scale: 1.05
      }} animate={{
        opacity: 1,
        scale: 1.1
      }} exit={{
        opacity: 0
      }} transition={{
        opacity: {
          duration: 1.5,
          ease: 'easeInOut'
        },
        scale: {
          duration: 10,
          ease: 'linear'
        }
      }} className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: `url(${heroImages[currentImageIndex]})`
      }} data-id="element-22" />
      </AnimatePresence>

      {/* Warm Overlay */}
      <div className="absolute inset-0" style={{
      background: 'linear-gradient(to bottom, rgba(44, 24, 16, 0.4) 0%, rgba(44, 24, 16, 0.7) 100%)'
    }} data-id="element-23" />

      {/* Milestone Banner */}
      <AnimatePresence data-id="element-24">
        {milestone && <MilestoneBanner milestone={milestone} data-id="element-25" />}
      </AnimatePresence>

      {/* Content */}
      <div className="relative z-10 text-center px-4" data-id="element-26">
        {/* Top Label */}
        <motion.p initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-white/70 text-sm md:text-base uppercase tracking-[0.3em] mb-8" data-id="element-27">
          Mallorca 2026
        </motion.p>

        {/* Countdown - Days dominant */}
        <div className="flex flex-col items-center mb-10" data-id="element-28">
          <CountdownCard value={timeLeft.days} label="Tage" delay={0.1} size="large" data-id="element-29" />

          <div className="flex gap-3 md:gap-5 mt-6 md:mt-8" data-id="element-30">
            <CountdownCard value={timeLeft.hours} label="Stunden" delay={0.2} size="normal" data-id="element-31" />
            <CountdownCard value={timeLeft.minutes} label="Minuten" delay={0.3} size="normal" data-id="element-32" />
            <CountdownCard value={timeLeft.seconds} label="Sekunden" delay={0.4} size="normal" data-id="element-33" />
          </div>
        </div>

        {/* Target Date */}
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.6,
        delay: 0.5
      }} className="font-serif text-xl md:text-2xl text-white/90 font-medium" data-id="element-34">
          20. August 2026
        </motion.p>
      </div>

      {/* Scroll Indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1,
      y: [0, 8, 0]
    }} transition={{
      opacity: {
        delay: 1,
        duration: 0.5
      },
      y: {
        delay: 1,
        duration: 1.5,
        repeat: Infinity
      }
    }} className="absolute bottom-8 left-1/2 -translate-x-1/2" data-id="element-35">
        <div className="w-6 h-10 rounded-full border-2 border-white/40 flex justify-center pt-2" data-id="element-36">
          <div className="w-1.5 h-3 bg-white/60 rounded-full" data-id="element-37" />
        </div>
      </motion.div>
    </section>;
}